import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { SidebarBrand, SwitchAppList } from "@/components/AppSidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Home as HomeIcon,
  Bell,
  Mail,
  User,
  LogOut,
  Heart,
  MessageCircle,
  MoreHorizontal,
  Trash2,
  BookOpen,
  BookMarked,
  Megaphone,
  HelpCircle,
  ChevronRight,
  ChevronDown,
  LayoutGrid,
  Search,
  X,
  Send,
  Image as ImageIcon,
  Youtube,
  PlayCircle,
  Hash,
  CalendarDays,
  History,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { formatDistanceToNow } from "date-fns";

// ── YouTube helpers ──────────────────────────────────────
const YT_REGEX =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

const extractVideoId = (text) => {
  const match = text.match(YT_REGEX);
  return match ? match[1] : null;
};

const ytThumbnail = (id) =>
  `https://img.youtube.com/vi/${id}/hqdefault.jpg`;

const ytEmbed = (url) => {
  const id = extractVideoId(url);
  return id ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1` : null;
};

const cleanVerseText = (verse) =>
  (verse?.text || verse?.passages?.[0] || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean)
    .join(" ")
    .replace(/\[\d+\]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const CATEGORIES = [
  { value: "prayer",       label: "Prayer",       color: "text-rose-600",   bg: "bg-rose-50",   border: "border-rose-200"   },
  { value: "praise",       label: "Questions",    color: "text-amber-600",  bg: "bg-amber-50",  border: "border-amber-200"  },
  { value: "verse",        label: "Verse",        color: "text-teal-600",   bg: "bg-teal-50",   border: "border-teal-200"   },
  { value: "sermon", label: "Sermon", color: "text-purple-600", bg: "bg-purple-50", border: "border-purple-200" },
];

const getInitials = (name) =>
  name?.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2) || "?";

const timeAgo = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "";
  }
};

function CategoryPill({ category, size = "sm" }) {
  const cat = CATEGORIES.find((c) => c.value === category);
  if (!cat || !category) return null;
  const textSize = size === "xs" ? "text-[10px]" : "text-xs";
  return (
    <span
      className={`inline-flex items-center gap-1 font-medium px-2 py-0.5 rounded-full border ${textSize} ${cat.bg} ${cat.color} ${cat.border}`}
    >
      {category === "prayer"       && <Heart className="w-3 h-3" />}
      {category === "praise"       && <HelpCircle className="w-3 h-3" />}
      {category === "verse"        && <BookOpen className="w-3 h-3" />}
      {category === "sermon" && <Megaphone className="w-3 h-3" />}
      {cat.label}
    </span>
  );
}

// ── YouTube embed component ──────────────────────────────
function YoutubeEmbed({ url, preview = null }) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = ytEmbed(url);
  const videoId = extractVideoId(url);
  const thumb = preview?.thumbnail || (videoId ? ytThumbnail(videoId) : null);

  if (!embedUrl) return null;

  return (
    <div className="rounded-xl overflow-hidden border border-slate-200 bg-black">
      {playing ? (
        <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
          <iframe
            className="absolute inset-0 w-full h-full"
            src={`${embedUrl}&autoplay=1`}
            title={preview?.title || "YouTube video"}
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <button
          className="relative w-full group"
          onClick={() => setPlaying(true)}
        >
          {/* Thumbnail */}
          {thumb ? (
            <img
              src={thumb}
              alt={preview?.title || "Video thumbnail"}
              className="w-full max-h-64 object-cover"
            />
          ) : (
            <div className="w-full h-44 bg-slate-800 flex items-center justify-center">
              <Youtube className="w-12 h-12 text-slate-600" />
            </div>
          )}
          {/* Play overlay */}
          <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
            <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <PlayCircle className="w-8 h-8 text-white fill-white" />
            </div>
          </div>
          {/* Title/channel overlay */}
          {(preview?.title || preview?.channelName) && (
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2.5 text-left">
              {preview.title && (
                <p className="text-white text-sm font-semibold leading-tight line-clamp-2">
                  {preview.title}
                </p>
              )}
              {preview.channelName && (
                <p className="text-white/70 text-xs mt-0.5">{preview.channelName}</p>
              )}
            </div>
          )}
        </button>
      )}
    </div>
  );
}

// ── Render content with clickable hashtags + styled @mentions ──────
function renderContent(text, onHashtagClick = () => {}) {
  if (!text) return null;
  return text.split(/([@#]\w+)/g).map((part, i) => {
    if (/^#\w+$/.test(part)) {
      return (
        <span
          key={`h-${i}`}
          data-testid={`hashtag-link-${part.slice(1)}`}
          onClick={(e) => { e.stopPropagation(); onHashtagClick(part.slice(1).toLowerCase()); }}
          className="text-amber-600 hover:text-amber-700 font-medium cursor-pointer hover:underline"
        >
          {part}
        </span>
      );
    }
    if (/^@\w+$/.test(part)) {
      return (
        <span
          key={`m-${i}`}
          data-testid={`mention-link-${part.slice(1)}`}
          className="text-indigo-600 font-medium"
        >
          {part}
        </span>
      );
    }
    return <span key={`t-${i}`}>{part}</span>;
  });
}

function PostCard({ post, currentUser, onLike, onDelete, onReplyClick, onHashtagClick }) {
  const canDelete =
    currentUser.role === "teacher" || post.author_id === currentUser.user_id;

  // Detect if the current user is tagged in this post
  const isMentioned = (() => {
    if (!currentUser?.name || !post.content) return false;
    const parts = currentUser.name.toLowerCase().split(/\s+/).filter(Boolean);
    const slug = parts.join("_");        // e.g. "josh_martin"
    const firstName = parts[0];           // backward compat: @josh
    return new RegExp(`@(${slug}|${firstName})\\b`, "i").test(post.content);
  })();

  return (
    <article
      data-testid={`feed-post-${post.post_id}`}
      className={`px-4 py-4 border-b transition-colors cursor-pointer group ${
        isMentioned
          ? "bg-indigo-50/50 border-indigo-100 hover:bg-indigo-50"
          : "border-slate-100 hover:bg-slate-50/60"
      }`}
      onClick={() => onReplyClick(post)}
    >
      <div className="flex gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={post.author_picture} />
          <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
            {getInitials(post.author_name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          {/* Header row */}
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              <span className="font-semibold text-slate-900 text-sm leading-none truncate">
                {post.author_name}
              </span>
              {post.author_role === "teacher" && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4 px-1.5 leading-none font-medium">
                  Teacher
                </Badge>
              )}
              {isMentioned && (
                <span
                  data-testid={`mentioned-badge-${post.post_id}`}
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full border border-indigo-200 shrink-0"
                >
                  <User className="w-2.5 h-2.5" />
                  Mentioned you
                </span>
              )}
              <span className="text-slate-400 text-xs shrink-0">
                {timeAgo(post.created_at)}
              </span>
            </div>

            {canDelete && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid={`post-menu-${post.post_id}`}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded-full hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-all shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="end"
                  className="w-44"
                  onClick={(e) => e.stopPropagation()}
                >
                  <DropdownMenuItem
                    data-testid={`delete-post-${post.post_id}`}
                    className="text-red-600 focus:text-red-700 focus:bg-red-50 gap-2"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(post.post_id);
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete post
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Category */}
          {post.category && (
            <div className="mb-2">
              <CategoryPill category={post.category} />
            </div>
          )}

          {/* Content */}
          <p className="text-slate-700 text-sm leading-relaxed whitespace-pre-wrap break-words">
            {renderContent(post.content, onHashtagClick)}
          </p>

          {/* Image */}
          {post.image_url && (
            <div className="mt-3 rounded-xl overflow-hidden border border-slate-200">
              <img
                src={post.image_url}
                alt="Post attachment"
                className="w-full max-h-96 object-cover"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* YouTube embed */}
          {post.youtube_url && !post.image_url && (
            <div className="mt-3" onClick={(e) => e.stopPropagation()}>
              <YoutubeEmbed url={post.youtube_url} />
            </div>
          )}

          {/* Actions */}
          <div
            className="flex items-center gap-5 mt-3"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              data-testid={`reply-btn-${post.post_id}`}
              className="flex items-center gap-1.5 text-slate-400 hover:text-sky-500 transition-colors group/btn"
              onClick={(e) => { e.stopPropagation(); onReplyClick(post); }}
            >
              <div className="p-1.5 rounded-full group-hover/btn:bg-sky-50 transition-colors">
                <MessageCircle className="w-4 h-4" />
              </div>
              <span className="text-xs font-medium">{post.reply_count || 0}</span>
            </button>

            <button
              data-testid={`like-btn-${post.post_id}`}
              className={`flex items-center gap-1.5 transition-colors group/btn ${
                post.liked_by_me
                  ? "text-rose-500"
                  : "text-slate-400 hover:text-rose-500"
              }`}
              onClick={(e) => { e.stopPropagation(); onLike(post.post_id); }}
            >
              <div className="p-1.5 rounded-full hover:bg-rose-50 transition-colors">
                <Heart
                  className={`w-4 h-4 ${post.liked_by_me ? "fill-current" : ""}`}
                />
              </div>
              <span className="text-xs font-medium">{post.like_count || 0}</span>
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

// ── Bible Verse Search Dialog ──────────────────────────
const TRANSLATIONS = [
  { id: "web",   label: "WEB",  description: "World English Bible",       source: "bible-api" },
  { id: "kjv",   label: "KJV",  description: "King James Version",        source: "bible-api" },
  { id: "esv",   label: "ESV",  description: "English Standard Version",  source: "esv-api" },
  { id: "asv",   label: "ASV",  description: "American Standard Version", source: "bible-api" },
  { id: "bbe",   label: "BBE",  description: "Bible in Basic English",    source: "bible-api" },
];

function BibleVerseDialog({ open, onClose, onInsert }) {
  const [query, setQuery] = useState("");
  const [translation, setTranslation] = useState("esv");
  const [verse, setVerse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const previewText = useMemo(() => (verse ? cleanVerseText(verse) : ""), [verse]);

  // Reset state whenever dialog opens
  useEffect(() => {
    if (open) {
      setQuery("");
      setVerse(null);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [open]);

  const fetchVerse = async () => {
    const ref = query.trim();
    if (!ref) return;
    setLoading(true);
    setError(null);
    setVerse(null);
    try {
      const selectedTranslation = TRANSLATIONS.find(t => t.id === translation) || TRANSLATIONS[0];

      if (selectedTranslation.source === "esv-api") {
        // ESV goes through our backend proxy
        const res = await fetch(`${API}/bible/esv?q=${encodeURIComponent(ref)}`, {
          credentials: "include",
        });
        if (res.status === 404) throw new Error("not_found");
        if (!res.ok) throw new Error("api_error");
        const data = await res.json();
        if (data.detail) throw new Error("not_found");
        setVerse(data);
      } else {
        // Free translations via bible-api.com
        const encoded = encodeURIComponent(ref.replace(/\s+/g, " "));
        const res = await fetch(
          `https://bible-api.com/${encoded}?translation=${translation}`
        );
        if (!res.ok) throw new Error("not_found");
        const data = await res.json();
        if (data.error) throw new Error("not_found");
        setVerse(data);
      }
    } catch (e) {
      if (e.message === "api_error") {
        setError("ESV API is temporarily unavailable. Try another translation.");
      } else {
        setError('Verse not found. Try a reference like "John 3:16" or "Psalm 23:1-3".');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInsert = () => {
    if (!verse) return;
    const cleanText = cleanVerseText(verse);
    const selectedT = TRANSLATIONS.find(t => t.id === translation);
    const translationLabel = verse.translation_note || selectedT?.label || translation.toUpperCase();
    const formatted = `"${cleanText}"\n— ${verse.reference || verse.canonical} (${translationLabel})`;
    onInsert(formatted);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg p-0 overflow-hidden rounded-2xl">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100">
          <DialogTitle className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <div className="w-7 h-7 rounded-lg bg-teal-50 flex items-center justify-center">
              <BookMarked className="w-4 h-4 text-teal-600" />
            </div>
            Add Bible Verse
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400 mt-1 ml-9">
            Search by reference — e.g. "John 3:16", "Psalm 23", "Romans 8:28-30"
          </DialogDescription>
        </div>

        <div className="px-6 py-4 space-y-4">
          {/* Search row */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <BookOpen className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 pointer-events-none" />
              <input
                ref={inputRef}
                data-testid="bible-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchVerse();
                  if (e.key === "Escape") onClose();
                }}
                placeholder="John 3:16, Psalm 23, Romans 8:28…"
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400 text-slate-900 placeholder-slate-400 transition"
              />
            </div>

            {/* Translation picker */}
            <select
              data-testid="bible-translation-select"
              value={translation}
              onChange={(e) => { setTranslation(e.target.value); setVerse(null); setError(null); }}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-400/40 focus:border-teal-400 text-slate-700 bg-white cursor-pointer transition"
            >
              {TRANSLATIONS.map((t) => (
                <option key={t.id} value={t.id} title={t.description}>{t.label}</option>
              ))}
            </select>

            <Button
              data-testid="bible-search-btn"
              onClick={fetchVerse}
              disabled={!query.trim() || loading}
              size="sm"
              className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg px-4 shrink-0 disabled:opacity-40"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                "Search"
              )}
            </Button>
          </div>

          {/* Error */}
          {error && (
            <p data-testid="bible-search-error" className="text-sm text-red-500 bg-red-50 px-3 py-2 rounded-lg">
              {error}
            </p>
          )}

          {/* Verse preview */}
          {verse && (
            <div
              data-testid="bible-verse-preview"
              className="rounded-xl border border-teal-100 bg-teal-50/50 p-4"
            >
              <p className="text-sm italic text-slate-700 leading-relaxed">
                "{previewText}"
              </p>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs font-semibold text-teal-700">
                  — {verse.reference || verse.canonical}
                </span>
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                  {verse.translation_note || TRANSLATIONS.find(t => t.id === translation)?.label || translation.toUpperCase()}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-slate-100 bg-slate-50/50">
          <Button
            data-testid="bible-cancel-btn"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-slate-600 hover:text-slate-900"
          >
            Cancel
          </Button>
          <Button
            data-testid="bible-insert-btn"
            size="sm"
            disabled={!verse}
            onClick={handleInsert}
            className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg px-5 disabled:opacity-40"
          >
            Insert Verse
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Composer({ user, onPost }) {
  const visibleCategories = useMemo(
    () => CATEGORIES.filter((c) => c.value !== "sermon" || user?.role === "teacher"),
    [user?.role]
  );
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [ytPreview, setYtPreview] = useState(null);
  const [ytLoading, setYtLoading] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const hashtagStartRef = useRef(-1);
  const mentionStartRef = useRef(-1);
  const [hashtagQuery, setHashtagQuery] = useState(null);
  const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  const [hashtagIndex, setHashtagIndex] = useState(0);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [bibleDialogOpen, setBibleDialogOpen] = useState(false);

  const charLimit = 500;

  // ── Detect YouTube URL in text ──
  const detectYoutube = async (text) => {
    const videoId = extractVideoId(text);
    if (!videoId) {
      setYtPreview(null);
      return;
    }
    if (ytPreview?.videoId === videoId) return; // already loaded
    setYtLoading(true);
    setYtPreview(null);
    try {
      const watchUrl = `https://www.youtube.com/watch?v=${videoId}`;
      const res = await fetch(
        `https://www.youtube.com/oembed?url=${encodeURIComponent(watchUrl)}&format=json`
      );
      if (!res.ok) throw new Error("oembed failed");
      const data = await res.json();
      setYtPreview({
        videoId,
        url: watchUrl,
        title: data.title,
        channelName: data.author_name,
        thumbnail: data.thumbnail_url,
      });
    } catch {
      // Fallback: show thumbnail without metadata
      setYtPreview({
        videoId,
        url: `https://www.youtube.com/watch?v=${videoId}`,
        title: null,
        channelName: null,
        thumbnail: ytThumbnail(videoId),
      });
    } finally {
      setYtLoading(false);
    }
  };

  const handleContentChange = (e) => {
    const val = e.target.value.slice(0, charLimit);
    setContent(val);
    detectYoutube(val);
    detectInlineToken(val, e.target.selectionStart);
  };

  // Single function handles both @mention and #hashtag detection
  const detectInlineToken = (val, cursor) => {
    const textUpToCursor = val.slice(0, cursor);

    // @mention has priority
    const mentionMatch = textUpToCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      mentionStartRef.current = textUpToCursor.lastIndexOf("@");
      const prefix = mentionMatch[1];
      setMentionQuery(prefix);
      fetchMentionSuggestions(prefix);
      setHashtagQuery(null);
      setHashtagSuggestions([]);
      return;
    }
    setMentionQuery(null);
    setMentionSuggestions([]);

    // Then #hashtag
    const hashtagMatch = textUpToCursor.match(/#(\w*)$/);
    if (hashtagMatch) {
      hashtagStartRef.current = textUpToCursor.lastIndexOf("#");
      const prefix = hashtagMatch[1].toLowerCase();
      setHashtagQuery(prefix);
      fetchHashtagSuggestions(prefix);
      return;
    }
    setHashtagQuery(null);
    setHashtagSuggestions([]);
  };

  const fetchMentionSuggestions = async (prefix) => {
    try {
      const { data } = await axios.get(
        `${API}/feed/community/members?q=${encodeURIComponent(prefix)}`
      );
      setMentionSuggestions(data);
      setMentionIndex(0);
    } catch {}
  };

  const insertMention = (member) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const start = mentionStartRef.current;
    const fullSlug = member.name.toLowerCase().replace(/\s+/g, "_"); // e.g. "sara_pastor"
    const newContent = (
      content.slice(0, start) + "@" + fullSlug + " " + content.slice(cursor)
    ).slice(0, charLimit);
    setContent(newContent);
    setMentionQuery(null);
    setMentionSuggestions([]);
    const newCursor = start + fullSlug.length + 2;
    setTimeout(() => {
      el.setSelectionRange(newCursor, newCursor);
      el.focus();
    }, 0);
  };

  const fetchHashtagSuggestions = async (prefix) => {
    try {
      const { data } = await axios.get(`${API}/feed/hashtags?q=${encodeURIComponent(prefix)}&limit=7`);
      setHashtagSuggestions(data);
      setHashtagIndex(0);
    } catch {}
  };

  const insertHashtag = (tag) => {
    const el = textareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const start = hashtagStartRef.current;
    const newContent = (content.slice(0, start) + '#' + tag + ' ' + content.slice(cursor)).slice(0, charLimit);
    setContent(newContent);
    setHashtagQuery(null);
    setHashtagSuggestions([]);
    const newCursor = start + tag.length + 2;
    setTimeout(() => {
      el.setSelectionRange(newCursor, newCursor);
      el.focus();
    }, 0);
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImagePreview(URL.createObjectURL(file));
    setImageFile(file);
    setUploadedUrl(null);
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await axios.post(`${API}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedUrl(data.url);
    } catch {
      toast.error("Image upload failed");
      setImagePreview(null);
      setImageFile(null);
    } finally {
      setUploading(false);
    }
    e.target.value = "";
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setUploadedUrl(null);
  };

  const handleBibleInsert = (verseText) => {
    const prefix = content.trim() ? content.trim() + "\n\n" : "";
    setContent((prefix + verseText).slice(0, charLimit));
    // Auto-select verse category
    setCategory("verse");
  };

  const handlePost = async () => {
    if (!content.trim() && !imagePreview && !ytPreview) return;
    if (uploading) { toast("Image still uploading…"); return; }
    setLoading(true);
    try {
      await onPost(content, category, uploadedUrl, ytPreview?.url || null);
      setContent("");
      setCategory(null);
      setImageFile(null);
      setImagePreview(null);
      setUploadedUrl(null);
      setYtPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const remaining = charLimit - content.length;

  return (
    <div className="p-4 border-b border-slate-100">
      <div className="flex gap-3">
        <Avatar className="h-10 w-10 shrink-0">
          <AvatarImage src={user?.picture} />
          <AvatarFallback className="bg-amber-100 text-amber-700 text-sm font-semibold">
            {getInitials(user?.name)}
          </AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="relative">
            <Textarea
              ref={textareaRef}
              data-testid="feed-composer-input"
              value={content}
              onChange={handleContentChange}
              placeholder="What's on your heart?"
              className="border-none shadow-none resize-none text-slate-900 placeholder-slate-400 text-sm focus-visible:ring-0 focus-visible:ring-offset-0 p-0 min-h-[64px] bg-transparent"
              onKeyDown={(e) => {
                if (mentionQuery !== null && mentionSuggestions.length > 0) {
                  if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionSuggestions.length); return; }
                  if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length); return; }
                  if (e.key === "Enter") { e.preventDefault(); if (mentionSuggestions[mentionIndex]) insertMention(mentionSuggestions[mentionIndex]); return; }
                  if (e.key === "Escape") { setMentionQuery(null); setMentionSuggestions([]); return; }
                }
                if (hashtagQuery !== null && hashtagSuggestions.length > 0) {
                  if (e.key === "ArrowDown") { e.preventDefault(); setHashtagIndex(i => (i + 1) % hashtagSuggestions.length); return; }
                  if (e.key === "ArrowUp") { e.preventDefault(); setHashtagIndex(i => (i - 1 + hashtagSuggestions.length) % hashtagSuggestions.length); return; }
                  if (e.key === "Enter") { e.preventDefault(); if (hashtagSuggestions[hashtagIndex]) insertHashtag(hashtagSuggestions[hashtagIndex].tag); return; }
                  if (e.key === "Escape") { setHashtagQuery(null); setHashtagSuggestions([]); return; }
                }
                if (e.key === "Enter" && e.metaKey) handlePost();
              }}
            />
            {/* @mention autocomplete dropdown */}
            {mentionQuery !== null && (
              <div
                data-testid="mention-dropdown"
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-52 overflow-y-auto"
              >
                {mentionSuggestions.length === 0 ? (
                  <div className="px-3 py-2.5 flex items-center gap-2 text-slate-400">
                    <User className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-xs">
                      {mentionQuery.length > 0 ? `No members matching "@${mentionQuery}"` : "Loading members…"}
                    </span>
                  </div>
                ) : (
                  mentionSuggestions.map((m, i) => (
                    <button
                      key={m.user_id}
                      data-testid={`mention-suggestion-${m.user_id}`}
                      onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                      className={`flex items-center gap-2.5 w-full px-3 py-2 text-left transition-colors ${
                        i === mentionIndex ? "bg-indigo-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <Avatar className="h-7 w-7 shrink-0">
                        <AvatarImage src={m.picture} />
                        <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">
                          {getInitials(m.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                        <p className="text-xs text-slate-400 capitalize">{m.role}</p>
                      </div>
                      {m.role === "teacher" && (
                        <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4 px-1.5 shrink-0">
                          Teacher
                        </Badge>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}

            {/* Hashtag autocomplete dropdown */}
            {hashtagQuery !== null && (
              <div
                data-testid="hashtag-dropdown"
                className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden"
              >
                {hashtagSuggestions.length === 0 ? (
                  <div className="px-3 py-2.5 flex items-center gap-2 text-slate-400">
                    <Hash className="w-3.5 h-3.5 shrink-0" />
                    <span className="text-xs">
                      {hashtagQuery.length > 0 ? `Create #${hashtagQuery}` : "Loading topics…"}
                    </span>
                  </div>
                ) : (
                  hashtagSuggestions.map((s, i) => (
                    <button
                      key={s.tag}
                      data-testid={`hashtag-suggestion-${s.tag}`}
                      onMouseDown={(e) => { e.preventDefault(); insertHashtag(s.tag); }}
                      className={`flex items-center justify-between w-full px-3 py-2 text-left transition-colors ${
                        i === hashtagIndex ? "bg-amber-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <span className="text-sm font-medium text-amber-600 flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5 shrink-0" />
                        {s.tag}
                      </span>
                      <span className="text-xs text-slate-400">{s.post_count} post{s.post_count !== 1 ? "s" : ""}</span>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Category pills */}
          <div className="flex flex-wrap gap-1.5 mt-1">
            {visibleCategories.map((cat) => (
              <button
                key={cat.value}
                data-testid={`category-${cat.value}`}
                onClick={() =>
                  setCategory(category === cat.value ? null : cat.value)
                }
                className={`text-xs px-2.5 py-1 rounded-full border font-medium transition-all ${
                  category === cat.value
                    ? `${cat.bg} ${cat.color} ${cat.border}`
                    : "border-slate-200 text-slate-500 hover:border-slate-300 bg-white"
                }`}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {/* YouTube preview */}
          {(ytLoading || ytPreview) && !imagePreview && (
            <div className="mt-3">
              {ytLoading ? (
                <div className="flex items-center gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-400 text-sm">
                  <div className="w-4 h-4 border-2 border-slate-300 border-t-red-500 rounded-full animate-spin shrink-0" />
                  Loading video preview…
                </div>
              ) : (
                <div className="relative rounded-xl overflow-hidden border border-slate-200">
                  <YoutubeEmbed url={ytPreview.url} preview={ytPreview} />
                  <button
                    onClick={() => { setYtPreview(null); }}
                    className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors z-10"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Image preview */}
          {imagePreview && (
            <div className="relative mt-3 rounded-xl overflow-hidden border border-slate-200 inline-block max-w-full">
              <img
                src={imagePreview}
                alt="Preview"
                className="max-h-56 max-w-full object-cover rounded-xl"
              />
              {uploading && (
                <div className="absolute inset-0 bg-white/60 flex items-center justify-center rounded-xl">
                  <div className="w-6 h-6 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin" />
                </div>
              )}
              <button
                onClick={removeImage}
                className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
            <div className="flex items-center gap-2">
              {/* Photo button */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                onChange={handleImageSelect}
                data-testid="image-file-input"
              />
              <button
                data-testid="attach-image-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="p-1.5 rounded-full text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors disabled:opacity-40"
                title="Add photo"
              >
                <ImageIcon className="w-5 h-5" />
              </button>

              {/* Bible verse button */}
              <button
                data-testid="add-bible-verse-btn"
                onClick={() => setBibleDialogOpen(true)}
                className="p-1.5 rounded-full text-slate-400 hover:text-teal-600 hover:bg-teal-50 transition-colors"
                title="Add Bible verse"
              >
                <BookMarked className="w-5 h-5" />
              </button>
              <span
                className={`text-xs ${
                  remaining < 20 ? "text-amber-500 font-medium" : "text-slate-400"
                }`}
              >
                {remaining} left
              </span>
            </div>
            <Button
              data-testid="feed-post-btn"
              size="sm"
              disabled={(!content.trim() && !imagePreview && !ytPreview) || loading || uploading}
              onClick={handlePost}
              className="bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-full px-5 h-8 shadow-sm shadow-amber-200 disabled:opacity-40"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                "Post"
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Bible verse dialog */}
      <BibleVerseDialog
        open={bibleDialogOpen}
        onClose={() => setBibleDialogOpen(false)}
        onInsert={handleBibleInsert}
      />
    </div>
  );
}

function ReplyDialog({ post, user, open, onClose, onCommentAdded }) {
  const [comments, setComments] = useState([]);
  const [reply, setReply] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const replyTextareaRef = useRef(null);
  const mentionStartRef = useRef(-1);
  const hashtagStartRef = useRef(-1);
  const [mentionQuery, setMentionQuery] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [hashtagQuery, setHashtagQuery] = useState(null);
  const [hashtagSuggestions, setHashtagSuggestions] = useState([]);
  const [hashtagIndex, setHashtagIndex] = useState(0);

  useEffect(() => {
    if (open && post) {
      setFetching(true);
      axios
        .get(`${API}/feed/${post.post_id}/comments`)
        .then((r) => setComments(r.data))
        .catch(() => {})
        .finally(() => setFetching(false));
    }
    if (!open) {
      setMentionQuery(null);
      setMentionSuggestions([]);
      setHashtagQuery(null);
      setHashtagSuggestions([]);
    }
  }, [open, post]);

  // Unified detector — @mention has priority over #hashtag
  const detectInlineToken = (val, cursor) => {
    const textUpToCursor = val.slice(0, cursor);

    const mentionMatch = textUpToCursor.match(/@(\w*)$/);
    if (mentionMatch) {
      mentionStartRef.current = textUpToCursor.lastIndexOf("@");
      setMentionQuery(mentionMatch[1]);
      fetchMentionSuggestions(mentionMatch[1]);
      setHashtagQuery(null);
      setHashtagSuggestions([]);
      return;
    }
    setMentionQuery(null);
    setMentionSuggestions([]);

    const hashtagMatch = textUpToCursor.match(/#(\w*)$/);
    if (hashtagMatch) {
      hashtagStartRef.current = textUpToCursor.lastIndexOf("#");
      const prefix = hashtagMatch[1].toLowerCase();
      setHashtagQuery(prefix);
      fetchHashtagSuggestions(prefix);
      return;
    }
    setHashtagQuery(null);
    setHashtagSuggestions([]);
  };

  const fetchMentionSuggestions = async (prefix) => {
    try {
      const { data } = await axios.get(
        `${API}/feed/community/members?q=${encodeURIComponent(prefix)}`
      );
      setMentionSuggestions(data);
      setMentionIndex(0);
    } catch {}
  };

  const fetchHashtagSuggestions = async (prefix) => {
    try {
      const { data } = await axios.get(
        `${API}/feed/hashtags?q=${encodeURIComponent(prefix)}&limit=7`
      );
      setHashtagSuggestions(data);
      setHashtagIndex(0);
    } catch {}
  };

  const insertMention = (member) => {
    const el = replyTextareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const start = mentionStartRef.current;
    const fullSlug = member.name.toLowerCase().replace(/\s+/g, "_");
    const newReply = (
      reply.slice(0, start) + "@" + fullSlug + " " + reply.slice(cursor)
    ).slice(0, 280);
    setReply(newReply);
    setMentionQuery(null);
    setMentionSuggestions([]);
    const newCursor = start + fullSlug.length + 2;
    setTimeout(() => {
      el.setSelectionRange(newCursor, newCursor);
      el.focus();
    }, 0);
  };

  const insertHashtag = (tag) => {
    const el = replyTextareaRef.current;
    if (!el) return;
    const cursor = el.selectionStart;
    const start = hashtagStartRef.current;
    const newReply = (
      reply.slice(0, start) + "#" + tag + " " + reply.slice(cursor)
    ).slice(0, 280);
    setReply(newReply);
    setHashtagQuery(null);
    setHashtagSuggestions([]);
    const newCursor = start + tag.length + 2;
    setTimeout(() => {
      el.setSelectionRange(newCursor, newCursor);
      el.focus();
    }, 0);
  };

  const handleReply = async () => {
    if (!reply.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/feed/${post.post_id}/comments`,
        { content: reply.trim() }
      );
      setComments((prev) => [...prev, data]);
      setReply("");
      onCommentAdded(post.post_id);
    } catch {
      toast.error("Failed to post reply");
    } finally {
      setLoading(false);
    }
  };

  if (!post) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-lg max-h-[85vh] flex flex-col p-0">
        <DialogHeader className="px-5 pt-5 pb-0 shrink-0">
          <DialogTitle className="text-slate-900 text-base font-semibold">
            Replies
          </DialogTitle>
          <DialogDescription className="sr-only">
            View and post replies to this community post
          </DialogDescription>
        </DialogHeader>

        {/* Original post */}
        <div className="px-5 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
          <div className="flex gap-3">
            <Avatar className="h-9 w-9 shrink-0">
              <AvatarImage src={post.author_picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {getInitials(post.author_name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-semibold text-slate-900 text-sm">
                  {post.author_name}
                </span>
                {post.author_role === "teacher" && (
                  <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4 px-1.5">
                    Teacher
                  </Badge>
                )}
                <span className="text-slate-400 text-xs">
                  {timeAgo(post.created_at)}
                </span>
              </div>
              {post.category && (
                <div className="mb-1.5">
                  <CategoryPill category={post.category} size="xs" />
                </div>
              )}
              <p className="text-slate-700 text-sm leading-relaxed">
                {renderContent(post.content)}
              </p>
              {post.image_url && (
                <img
                  src={post.image_url}
                  alt="Post attachment"
                  className="mt-2 rounded-lg max-h-40 object-cover border border-slate-200"
                />
              )}
              {post.youtube_url && !post.image_url && (
                <div className="mt-2">
                  <YoutubeEmbed url={post.youtube_url} />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Comments list */}
        <div className="flex-1 overflow-y-auto">
          {fetching ? (
            <div className="flex items-center justify-center py-10">
              <div className="w-6 h-6 border-2 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : comments.length === 0 ? (
            <p className="text-center text-slate-400 py-10 text-sm">
              No replies yet — be the first!
            </p>
          ) : (
            comments.map((c) => {
              const isCommentMentioned = (() => {
                if (!user?.name || !c.content) return false;
                const parts = user.name.toLowerCase().split(/\s+/).filter(Boolean);
                const slug = parts.join("_");
                const firstName = parts[0];
                return new RegExp(`@(${slug}|${firstName})\\b`, "i").test(c.content);
              })();
              return (
                <div
                  key={c.comment_id}
                  data-testid={`comment-${c.comment_id}`}
                  className={`px-5 py-3 border-b border-slate-100 last:border-0 transition-colors ${
                    isCommentMentioned ? "bg-indigo-50/50" : ""
                  }`}
                >
                  <div className="flex gap-3">
                    <Avatar className="h-8 w-8 shrink-0">
                      <AvatarImage src={c.author_picture} />
                      <AvatarFallback className="bg-slate-100 text-slate-600 text-xs font-semibold">
                        {getInitials(c.author_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="font-semibold text-slate-900 text-sm">
                          {c.author_name}
                        </span>
                        {isCommentMentioned && (
                          <span
                            data-testid={`comment-mentioned-badge-${c.comment_id}`}
                            className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded-full border border-indigo-200 shrink-0"
                          >
                            <User className="w-2.5 h-2.5" />
                            Mentioned you
                          </span>
                        )}
                        <span className="text-slate-400 text-xs">
                          {timeAgo(c.created_at)}
                        </span>
                      </div>
                      <p className="text-slate-700 text-sm leading-relaxed">
                        {renderContent(c.content)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Reply input */}
        <div className="px-5 py-4 border-t border-slate-100 bg-white shrink-0">
          <div className="flex gap-3 items-end">
            <Avatar className="h-8 w-8 shrink-0">
              <AvatarImage src={user?.picture} />
              <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 relative">
              {/* @mention dropdown for comments */}
              {mentionQuery !== null && (
                <div
                  data-testid="comment-mention-dropdown"
                  className="absolute z-50 bottom-full left-0 right-0 mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto"
                >
                  {mentionSuggestions.length === 0 ? (
                    <div className="px-3 py-2.5 flex items-center gap-2 text-slate-400">
                      <User className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-xs">
                        {mentionQuery.length > 0
                          ? `No members matching "@${mentionQuery}"`
                          : "Loading members…"}
                      </span>
                    </div>
                  ) : (
                    mentionSuggestions.map((m, i) => (
                      <button
                        key={m.user_id}
                        data-testid={`comment-mention-suggestion-${m.user_id}`}
                        onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                        className={`flex items-center gap-2.5 w-full px-3 py-2 text-left transition-colors ${
                          i === mentionIndex ? "bg-indigo-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <Avatar className="h-7 w-7 shrink-0">
                          <AvatarImage src={m.picture} />
                          <AvatarFallback className="bg-indigo-100 text-indigo-700 text-xs font-semibold">
                            {getInitials(m.name)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{m.name}</p>
                          <p className="text-xs text-slate-400 capitalize">{m.role}</p>
                        </div>
                        {m.role === "teacher" && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] h-4 px-1.5 shrink-0">
                            Teacher
                          </Badge>
                        )}
                      </button>
                    ))
                  )}
                </div>
              )}

              {/* #hashtag dropdown for comments */}
              {hashtagQuery !== null && (
                <div
                  data-testid="comment-hashtag-dropdown"
                  className="absolute z-50 bottom-full left-0 right-0 mb-1 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden max-h-48 overflow-y-auto"
                >
                  {hashtagSuggestions.length === 0 ? (
                    <div className="px-3 py-2.5 flex items-center gap-2 text-slate-400">
                      <Hash className="w-3.5 h-3.5 shrink-0" />
                      <span className="text-xs">
                        {hashtagQuery.length > 0 ? `Create #${hashtagQuery}` : "Loading topics…"}
                      </span>
                    </div>
                  ) : (
                    hashtagSuggestions.map((s, i) => (
                      <button
                        key={s.tag}
                        data-testid={`comment-hashtag-suggestion-${s.tag}`}
                        onMouseDown={(e) => { e.preventDefault(); insertHashtag(s.tag); }}
                        className={`flex items-center justify-between w-full px-3 py-2 text-left transition-colors ${
                          i === hashtagIndex ? "bg-amber-50" : "hover:bg-slate-50"
                        }`}
                      >
                        <span className="text-sm font-medium text-amber-600 flex items-center gap-1.5">
                          <Hash className="w-3.5 h-3.5 shrink-0" />
                          {s.tag}
                        </span>
                        <span className="text-xs text-slate-400">{s.post_count} post{s.post_count !== 1 ? "s" : ""}</span>
                      </button>
                    ))
                  )}
                </div>
              )}

              <Textarea
                ref={replyTextareaRef}
                data-testid="reply-input"
                value={reply}
                onChange={(e) => {
                  const val = e.target.value.slice(0, 280);
                  setReply(val);
                  detectInlineToken(val, e.target.selectionStart);
                }}
                onKeyDown={(e) => {
                  if (mentionQuery !== null && mentionSuggestions.length > 0) {
                    if (e.key === "ArrowDown") { e.preventDefault(); setMentionIndex(i => (i + 1) % mentionSuggestions.length); return; }
                    if (e.key === "ArrowUp") { e.preventDefault(); setMentionIndex(i => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length); return; }
                    if (e.key === "Enter") { e.preventDefault(); if (mentionSuggestions[mentionIndex]) insertMention(mentionSuggestions[mentionIndex]); return; }
                    if (e.key === "Escape") { e.stopPropagation(); setMentionQuery(null); setMentionSuggestions([]); return; }
                  }
                  if (hashtagQuery !== null && hashtagSuggestions.length > 0) {
                    if (e.key === "ArrowDown") { e.preventDefault(); setHashtagIndex(i => (i + 1) % hashtagSuggestions.length); return; }
                    if (e.key === "ArrowUp") { e.preventDefault(); setHashtagIndex(i => (i - 1 + hashtagSuggestions.length) % hashtagSuggestions.length); return; }
                    if (e.key === "Enter") { e.preventDefault(); if (hashtagSuggestions[hashtagIndex]) insertHashtag(hashtagSuggestions[hashtagIndex].tag); return; }
                    if (e.key === "Escape") { e.stopPropagation(); setHashtagQuery(null); setHashtagSuggestions([]); return; }
                  }
                  if (e.key === "Enter" && e.metaKey) handleReply();
                }}
                placeholder="Reply… type @ to mention, # for topics"
                className="w-full resize-none text-sm text-slate-900 placeholder-slate-400 border-slate-200 focus-visible:ring-amber-400 min-h-[40px]"
                rows={2}
              />
            </div>
            <Button
              data-testid="submit-reply-btn"
              size="icon"
              disabled={!reply.trim() || loading}
              onClick={handleReply}
              className="h-9 w-9 bg-amber-500 hover:bg-amber-600 text-white rounded-full shrink-0 shadow-sm disabled:opacity-40"
            >
              {loading ? (
                <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Home({ user }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [activeHashtag, setActiveHashtag] = useState(null);
  const [replyPost, setReplyPost] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [trendingHashtags, setTrendingHashtags] = useState([]);

  // Open a specific post when navigated from the notifications page
  useEffect(() => {
    if (location.state?.openPostId && posts.length > 0) {
      const target = posts.find((p) => p.post_id === location.state.openPostId);
      if (target) {
        setReplyPost(target);
        // Clear the state so it doesn't re-trigger
        window.history.replaceState({}, "");
      }
    }
  }, [location.state, posts]);

  const fetchFeed = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeTab !== "all") params.set("category", activeTab);
      if (activeHashtag) params.set("hashtag", activeHashtag);
      const q = params.toString();
      const { data } = await axios.get(`${API}/feed${q ? `?${q}` : ""}`);
      setPosts(data);
    } catch {
      toast.error("Failed to load feed");
    } finally {
      setLoading(false);
    }
  }, [activeTab, activeHashtag]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  useEffect(() => {
    fetchUnreadCount();
    fetchTrendingHashtags();
    axios.post(`${API}/feed/mark-viewed`).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTrendingHashtags = async () => {
    try {
      const { data } = await axios.get(`${API}/feed/hashtags?limit=8`);
      setTrendingHashtags(data);
    } catch {}
  };

  const fetchUnreadCount = async () => {
    try {
      const { data } = await axios.get(`${API}/community/notifications/unread-count`);
      setUnreadCount(data.count || 0);
    } catch {}
  };

  const handlePost = async (content, category, imageUrl, youtubeUrl) => {
    try {
      const { data } = await axios.post(`${API}/feed`, {
        content,
        category,
        image_url: imageUrl || null,
        youtube_url: youtubeUrl || null,
      });
      setPosts((prev) => [data, ...prev]);
      toast.success("Posted!");
    } catch {
      toast.error("Failed to post");
    }
  };

  const handleLike = async (postId) => {
    try {
      const { data } = await axios.post(`${API}/feed/${postId}/like`);
      setPosts((prev) =>
        prev.map((p) =>
          p.post_id === postId
            ? { ...p, likes: data.likes, like_count: data.like_count, liked_by_me: data.liked_by_me }
            : p
        )
      );
    } catch {
      toast.error("Failed to update like");
    }
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/feed/${postId}`);
      setPosts((prev) => prev.filter((p) => p.post_id !== postId));
      toast.success("Post deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleCommentAdded = (postId) => {
    setPosts((prev) =>
      prev.map((p) =>
        p.post_id === postId
          ? { ...p, reply_count: (p.reply_count || 0) + 1 }
          : p
      )
    );
  };

  const handleHashtagClick = (tag) => {
    setActiveHashtag((prev) => (prev === tag ? null : tag));
    setActiveTab("all");
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } finally {
      navigate("/");
    }
  };

  const filteredPosts = searchQuery
    ? posts.filter(
        (p) =>
          p.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
          p.author_name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : posts;

  const TABS = [
    { key: "all",          label: "For You"      },
    { key: "prayer",       label: "Prayer"       },
    { key: "praise",       label: "Questions"    },
    { key: "verse",        label: "Verse"        },
    ...(user?.role === "teacher"
      ? [{ key: "sermon", label: "Sermons" }]
      : []),
  ];

  // ——— Sidebar nav item ———
  const NavItem = ({ icon: Icon, label, onClick, active = false, badge = 0 }) => (
    <button
      data-testid={`home-nav-${label.toLowerCase().replace(/\s/g, "-")}`}
      onClick={onClick}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors w-full text-left group ${
        active
          ? "bg-amber-50 text-amber-700 font-semibold"
          : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
      }`}
    >
      <div className="relative">
        <Icon
          className={`w-[18px] h-[18px] shrink-0 ${
            active ? "text-amber-600" : "text-slate-400 group-hover:text-slate-600"
          }`}
        />
        {badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[14px] h-3.5 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center px-0.5">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </div>
      <span className="text-sm hidden xl:block">{label}</span>
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="flex md:ml-[68px] xl:ml-[260px] justify-center">

        {/* ===== LEFT SIDEBAR ===== */}
        <aside className="hidden md:flex flex-col fixed top-0 left-0 h-screen w-[68px] xl:w-[260px] shrink-0 bg-white border-r border-slate-200 px-2 xl:px-4 py-4 overflow-y-auto z-40">
          {/* App Switcher */}
          <div className="h-16 shrink-0 flex items-center px-1 mb-1">
            <SidebarBrand brandLabel="Community" />
          </div>

          <SwitchAppList user={user} active="community" />

          <Separator className="mb-2" />

          <p className="px-2.5 mb-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider hidden xl:block">
            Current App
          </p>

          {/* Navigation */}
          <nav className="flex flex-col gap-0.5 flex-1 relative">
            <NavItem
              icon={HomeIcon}
              label="Home"
              active
              onClick={() => {}}
            />
            <NavItem
              icon={Bell}
              label="Notifications"
              badge={unreadCount}
              onClick={() => navigate("/community/notifications")}
            />
            <NavItem
              icon={Mail}
              label="Messages"
              onClick={() => navigate("/community/messages")}
            />
            <NavItem
              icon={User}
              label="Profile"
              onClick={() => navigate("/community/profile")}
            />
          </nav>

          {/* Separator */}
          <div className="my-2 border-t border-slate-100" />

          {/* User area */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="home-user-menu"
                className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors w-full"
              >
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-amber-100 text-amber-700 text-xs font-semibold">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden xl:block text-left min-w-0">
                  <p className="text-sm font-semibold text-slate-900 truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-slate-500 capitalize">
                    {user?.role}
                  </p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              side="top"
              className="w-52"
            >
              <DropdownMenuItem
                onClick={() => navigate("/community/profile")}
                className="gap-2"
              >
                <User className="w-4 h-4" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="sidebar-versions-link"
                onClick={() => navigate("/versions")}
                className="gap-2"
              >
                <History className="w-4 h-4" />
                Versions
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={handleLogout}
                className="gap-2 text-red-600 focus:text-red-700 focus:bg-red-50"
              >
                <LogOut className="w-4 h-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </aside>

        {/* ===== MAIN FEED ===== */}
        <div className="flex w-full max-w-5xl">
        <main className="flex-1 min-w-0 border-x border-slate-200 bg-white">
          {/* Header + tabs */}
          <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b border-slate-100">
            <div className="px-5 pt-4">
              <h1 className="font-serif text-xl font-bold text-slate-900 mb-3">
                Community
              </h1>
            </div>
            <div className="flex" data-testid="feed-tabs">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  data-testid={`feed-tab-${tab.key}`}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-1 py-3 text-sm font-medium transition-colors relative hover:bg-slate-50 ${
                    activeTab === tab.key
                      ? "text-slate-900"
                      : "text-slate-500"
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.key && (
                    <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-12 h-[3px] bg-amber-500 rounded-full" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Hashtag filter banner */}
          {activeHashtag && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 border-b border-amber-100">
              <Hash className="w-3.5 h-3.5 text-amber-500 shrink-0" />
              <span className="text-xs text-amber-700 font-medium">Showing posts tagged</span>
              <span
                data-testid="active-hashtag-badge"
                className="text-xs font-bold text-amber-600 bg-amber-100 px-2 py-0.5 rounded-full"
              >
                #{activeHashtag}
              </span>
              <button
                data-testid="clear-hashtag-filter"
                onClick={() => setActiveHashtag(null)}
                className="ml-auto p-0.5 text-amber-400 hover:text-amber-700 transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Composer */}
          <Composer user={user} onPost={handlePost} />

          {/* Feed */}
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-slate-200 border-t-amber-500 rounded-full animate-spin" />
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-8 text-center">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                <MessageCircle className="w-7 h-7 text-slate-400" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-slate-900 mb-1">
                {activeTab === "all" ? "Nothing yet" : `No ${activeTab} posts`}
              </h3>
              <p className="text-slate-500 text-sm max-w-xs">
                {activeTab === "all"
                  ? "Be the first to share something with the community."
                  : "No posts in this category yet — share something!"}
              </p>
            </div>
          ) : (
            <div data-testid="feed-posts-list">
              {filteredPosts.map((post) => (
                <PostCard
                  key={post.post_id}
                  post={post}
                  currentUser={user}
                  onLike={handleLike}
                  onDelete={handleDelete}
                  onReplyClick={setReplyPost}
                  onHashtagClick={handleHashtagClick}
                />
              ))}
            </div>
          )}
        </main>

        {/* ===== RIGHT SIDEBAR ===== */}
        <aside className="hidden lg:flex flex-col sticky top-0 h-screen w-[320px] shrink-0 px-4 py-4 overflow-y-auto bg-slate-50">
          {/* Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <input
              data-testid="feed-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search posts…"
              className="w-full bg-white text-slate-900 placeholder-slate-400 border border-slate-200 rounded-full py-2.5 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/40 focus:border-amber-400 transition shadow-sm"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Trending categories */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
            <h2 className="font-serif font-semibold text-slate-900 text-sm mb-3">
              What's trending
            </h2>
            <div className="space-y-1">
              {[
                { key: "prayer",       icon: Heart,     label: "Prayer Requests", color: "text-rose-600",   bg: "bg-rose-50"   },
                { key: "praise",       icon: HelpCircle, label: "Questions",       color: "text-amber-600",  bg: "bg-amber-50"  },
                { key: "verse",        icon: BookOpen,  label: "Bible Verses",    color: "text-teal-600",   bg: "bg-teal-50"   },
                ...(user?.role === "teacher"
                  ? [{ key: "sermon", icon: Megaphone, label: "Sermons", color: "text-purple-600", bg: "bg-purple-50" }]
                  : []),
              ].map(({ key, icon: Icon, label, color, bg }) => (
                <button
                  key={key}
                  data-testid={`trending-${key}`}
                  onClick={() => setActiveTab(key)}
                  className="flex items-center gap-3 w-full px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${bg}`}>
                    <Icon className={`w-4 h-4 ${color}`} />
                  </div>
                  <div>
                    <p className={`text-xs font-semibold ${color}`}>{label}</p>
                    <p className="text-[11px] text-slate-400">
                      {posts.filter((p) => p.category === key).length} posts
                    </p>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-300 ml-auto" />
                </button>
              ))}
            </div>
          </div>

          {/* Trending Hashtags */}
          {trendingHashtags.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 mb-4">
              <h2 className="font-serif font-semibold text-slate-900 text-sm mb-3 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-amber-500" />
                Trending Topics
              </h2>
              <div className="space-y-0.5">
                {trendingHashtags.map((h) => (
                  <button
                    key={h.tag}
                    data-testid={`trending-hashtag-${h.tag}`}
                    onClick={() => handleHashtagClick(h.tag)}
                    className={`flex items-center justify-between w-full px-2 py-2 rounded-lg transition-colors text-left ${
                      activeHashtag === h.tag ? "bg-amber-50" : "hover:bg-slate-50"
                    }`}
                  >
                    <span className={`text-sm font-medium ${activeHashtag === h.tag ? "text-amber-600" : "text-slate-700"}`}>
                      #{h.tag}
                    </span>
                    <span className="text-[11px] text-slate-400">{h.post_count} post{h.post_count !== 1 ? "s" : ""}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <p className="mt-auto pt-4 text-[11px] text-slate-400 px-1">
            Live Church — Growing disciples, one lesson at a time.
          </p>
        </aside>
        </div>{/* max-w-5xl wrapper */}
      </div>

      {/* ===== MOBILE BOTTOM BAR ===== */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 flex items-center justify-around px-4 py-2 z-50">
        <button
          onClick={() => {}}
          className="p-2 text-amber-600"
        >
          <HomeIcon className="w-6 h-6" />
        </button>
        <button
          onClick={() => navigate("/notifications")}
          className="p-2 text-slate-400"
        >
          <Bell className="w-6 h-6" />
        </button>
        <button
          onClick={() => navigate("/messages")}
          className="p-2 text-slate-400"
        >
          <Mail className="w-6 h-6" />
        </button>
        <button
          onClick={() => navigate("/community/profile")}
          className="p-2 text-slate-400"
        >
          <User className="w-6 h-6" />
        </button>
      </nav>

      {/* Reply Dialog */}
      <ReplyDialog
        post={replyPost}
        user={user}
        open={!!replyPost}
        onClose={() => setReplyPost(null)}
        onCommentAdded={handleCommentAdded}
      />
    </div>
  );
}
